import posthog from 'posthog-js'

// let _posthogSessionReplayEnabled = false

function isLogRocketEnabled(): boolean {
  return !!process.env.LOGROCKET_APP_ID
}

function isPostHogEnabled(): boolean {
  return !!process.env.POSTHOG_PROJECT_TOKEN
}

async function getLogRocket() {
  const { default: LogRocket } = await import('logrocket')
  return LogRocket
}

/**
 * Call once at app startup (after PostHogProvider mounts and user is identified)
 * to conditionally start session recording based on the LaunchDarkly flag.
 */
export function initAnalytics(sessionReplayEnabled?: boolean): void {
  if (process.env.POSTHOG_PROJECT_TOKEN && sessionReplayEnabled) {
    posthog.startSessionRecording()
  }
}

export interface PostHogClientContext {
  /** The browser SDK's current distinct id (the identified user id once PostHogIdentify runs). */
  distinctId?: string
  /** The browser SDK's current `$session_id` — what ties a server event to this session replay. */
  sessionId?: string
}

/**
 * The browser SDK's current identity + session, for handing to the API so an event the *server*
 * captures still lands on this user and shows up in this session's replay.
 *
 * Without it, a server-side event carries no `$session_id` and a synthetic distinct id, so it is
 * invisible both in the replay's event list and when filtering by the person who caused it — the
 * event looks like it never fired even when it did (ENG-766). Same data PostHog's `tracing_headers`
 * option would send as `X-POSTHOG-{DISTINCT,SESSION}-ID`; passed explicitly because only a couple
 * of mutations need it.
 *
 * Returns an empty object when PostHog isn't configured (local/E2E) or the SDK isn't ready.
 */
export function getPostHogClientContext(): PostHogClientContext {
  if (!isPostHogEnabled()) return {}
  try {
    return {
      distinctId: posthog.get_distinct_id() ?? undefined,
      // Undefined until session recording has a session; the caller sends what it gets.
      sessionId: posthog.get_session_id() ?? undefined
    }
  } catch {
    // Same contract as `track` below — analytics plumbing never breaks a caller's flow.
    return {}
  }
}

export function track(eventName: string, properties?: Record<string, any>): void {
  // Analytics is fire-and-forget: never let a tracking failure (uninitialised
  // client, network error, etc.) bubble into a caller's flow and break it.
  try {
    if (isLogRocketEnabled()) {
      getLogRocket().then((LR) => LR.track(eventName))
    }
    if (isPostHogEnabled()) {
      posthog.capture(eventName, properties)
    }
  } catch {
    // swallow — a missed metric must never interrupt user-facing behaviour
  }
}

export function info(message: string | object, data?: any): void {
  if (isLogRocketEnabled()) {
    getLogRocket().then((LR) => LR.info(message as any, data))
  }
  if (isPostHogEnabled()) {
    const eventName = typeof message === 'string' ? message : 'info'
    const eventData = typeof message === 'object' ? message : data != null ? { data } : undefined
    posthog.capture(`info:${eventName}`, eventData)
  }
}

export function error(message: string, data?: any): void {
  if (isLogRocketEnabled()) {
    getLogRocket().then((LR) => LR.error(message, data))
  }
  if (isPostHogEnabled()) {
    posthog.capture(`error:${message}`, { data, level: 'error' })
  }
}

export function captureMessage(message: string): void {
  if (isLogRocketEnabled()) {
    getLogRocket().then((LR) => LR.captureMessage(message))
  }
  if (isPostHogEnabled()) {
    posthog.capture(message)
  }
}
